import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import {
  Activity,
  AlertCircle,
  Bot,
  Building2,
  ChartLine,
  Coins,
  Columns3,
  Cpu,
  FileAudio,
  FileText,
  FileVideo,
  Folder,
  Image,
  Inbox,
  Lightbulb,
  ListOrdered,
  ListVideo,
  LogIn,
  Mail,
  Megaphone,
  MessageCircle,
  Play,
  StickyNote,
  Table as TableIcon,
  Trophy,
  TrendingUp,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import { PieChart } from "@mui/x-charts/PieChart";
import axios from "axios";
import { hostname, parseError } from "../../utils";

interface AdminAnalyticsProps {
  getToken: () => Promise<string>;
}

interface AnalyticsData {
  period: {
    start: string;
    end: string;
    labels: string[];
  };
  summary: {
    totalUsers: number;
    activeUsers30d: number;
    totalCompanies: number;
    newCompanies30d: number;
    totalSignups: number;
    totalLogins: number;
    totalMagicLinks: number;
    totalConversions: number;
    conversionRate: number;
    totalCredits: number;
    totalApiRequests: number;
    totalErrors: number;
    errorRate: number;
    totalSmartLists: number;
    totalSmartRows: number;
    totalAgents: number;
    totalAgentConversations: number;
    totalSequences: number;
    totalSequenceMembers: number;
    totalSequenceSteps: number;
    totalSequenceExecutions: number;
    totalInboxes: number;
    totalInboxMessages: number;
    totalDocuments: number;
    totalDocumentTokens: number;
    totalDocumentCharacters: number;
    totalContent: number;
    totalContentStrategies: number;
    totalContentPillars: number;
    totalContentCampaigns: number;
    totalImages: number;
    totalAudio: number;
    totalVideos: number;
  };
  timeSeries: {
    signups: number[];
    logins: number[];
    magicLinks: number[];
    conversions: number[];
    credits: number[];
    apiRequests: number[];
    errors: number[];
  };
  breakdown: {
    features: { feature: string; credits: number; requests: number }[];
    models: { model: string; credits: number; tokens: number; requests: number }[];
    subscriptions: { type: string; count: number }[];
    topUsers: { uid: string; email: string; displayName: string; credits: number; requests: number }[];
    topErrorEndpoints: { path: string; method: string; statusCode: number; count: number }[];
    recentSignups: { uid: string; email: string; displayName: string; createdDate: string }[];
    recentLogins: { uid: string; email: string; displayName: string; loginDate: string; ipAddress: string }[];
  };
}

interface ApiHealthData {
  period: {
    start: string;
    end: string;
    days: number;
    labels: string[];
  };
  summary: {
    totalApiRequests: number;
    totalErrors: number;
    errorRate: number;
  };
  timeSeries: {
    apiRequests: number[];
    errors: number[];
  };
  topErrorEndpoints: { path: string; method: string; statusCode: number; count: number }[];
}

interface WhitelabelLeaderboardEntry {
  id: string;
  name: string;
  domain: string | null;
  companies: number;
  users: number;
  creditsUsed: number;
  creditsCurrentMonth: number;
  creditsLastMonth: number;
}

interface WhitelabelLeaderboardData {
  leaderboard: WhitelabelLeaderboardEntry[];
  totalWhitelabels: number;
}

interface InboxMessagesData {
  period: {
    start: string;
    end: string;
    hours: number;
    labels: string[];
  };
  summary: {
    totalMessages: number;
  };
  timeSeries: {
    messages: number[];
  };
}

interface PublicApiUsageData {
  period: {
    start: string;
    end: string;
    days: number;
    labels: string[];
  };
  summary: {
    totalRequests: number;
    totalErrors: number;
    errorRate: number;
  };
  timeSeries: {
    requests: number[];
  };
  topEndpoints: { path: string; method: string; requests: number; errors: number; avgDurationMs: number }[];
  topUsers: { uid: string; email: string; displayName: string; requests: number }[];
  topCompanies: { companyId: string; name: string; requests: number }[];
}

const MetricCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  color?: string;
}> = ({ icon, title, value, subtitle, color = "primary.main" }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ height: "100%", display: "flex", alignItems: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

const formatCredits = (credits: number): string => {
  return credits.toFixed(1);
};

const AdminAnalytics: React.FC<AdminAnalyticsProps> = ({ getToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsData | null>(null);

  // API Health separate state with date filter
  const [apiHealthDays, setApiHealthDays] = useState(7);
  const [apiHealthLoading, setApiHealthLoading] = useState(false);
  const [apiHealthData, setApiHealthData] = useState<ApiHealthData | null>(null);

  // Whitelabel Leaderboard state
  const [whitelabelLoading, setWhitelabelLoading] = useState(false);
  const [whitelabelData, setWhitelabelData] = useState<WhitelabelLeaderboardData | null>(null);

  // Inbox Messages state
  const [inboxMessagesLoading, setInboxMessagesLoading] = useState(false);
  const [inboxMessagesData, setInboxMessagesData] = useState<InboxMessagesData | null>(null);

  // Public API Usage state
  const [publicApiDays, setPublicApiDays] = useState(7);
  const [publicApiLoading, setPublicApiLoading] = useState(false);
  const [publicApiData, setPublicApiData] = useState<PublicApiUsageData | null>(null);

  const fetchApiHealth = async (days: number) => {
    try {
      setApiHealthLoading(true);
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/admin/analytics/api-health?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApiHealthData(response.data);
    } catch (err) {
      console.error("Failed to fetch API health:", parseError(err));
    } finally {
      setApiHealthLoading(false);
    }
  };

  const fetchWhitelabelLeaderboard = async () => {
    try {
      setWhitelabelLoading(true);
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/admin/analytics/whitelabel-leaderboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWhitelabelData(response.data);
    } catch (err) {
      console.error("Failed to fetch whitelabel leaderboard:", parseError(err));
    } finally {
      setWhitelabelLoading(false);
    }
  };

  const fetchInboxMessages = async () => {
    try {
      setInboxMessagesLoading(true);
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/admin/analytics/inbox-messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInboxMessagesData(response.data);
    } catch (err) {
      console.error("Failed to fetch inbox messages:", parseError(err));
    } finally {
      setInboxMessagesLoading(false);
    }
  };

  const fetchPublicApiUsage = async (days: number) => {
    try {
      setPublicApiLoading(true);
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/admin/analytics/public-api-usage?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPublicApiData(response.data);
    } catch (err) {
      console.error("Failed to fetch public API usage:", parseError(err));
    } finally {
      setPublicApiLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchApiHealth(apiHealthDays);
    fetchWhitelabelLeaderboard();
    fetchInboxMessages();
    fetchPublicApiUsage(publicApiDays);
  }, []);

  useEffect(() => {
    fetchApiHealth(apiHealthDays);
  }, [apiHealthDays]);

  useEffect(() => {
    fetchPublicApiUsage(publicApiDays);
  }, [publicApiDays]);

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Platform Analytics
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Platform Analytics
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  // Format x-axis labels to show shorter dates
  const shortLabels = data.period.labels.map((label) => {
    const date = new Date(label);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  });

  // Subscription colors
  const subscriptionColors = [
    "#1976d2", // Primary blue
    "#388e3c", // Green
    "#f57c00", // Orange
    "#7b1fa2", // Purple
    "#d32f2f", // Red
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Platform Analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Last 30 days ({new Date(data.period.start).toLocaleDateString()} -{" "}
        {new Date(data.period.end).toLocaleDateString()})
      </Typography>

      {/* Company Metrics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Building2 size={35} />}
            title="Total Companies"
            value={formatNumber(data.summary.totalCompanies)}
            subtitle="Active companies"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Building2 size={35} />}
            title="New Companies"
            value={formatNumber(data.summary.newCompanies30d)}
            subtitle="Last 30 days"
            color="info.main"
          />
        </Grid>
      </Grid>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<User size={35} />}
            title="Total Users"
            value={formatNumber(data.summary.totalUsers)}
            subtitle={`${data.summary.activeUsers30d} active in 30d`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<UserPlus size={35} />}
            title="New Signups"
            value={formatNumber(data.summary.totalSignups)}
            subtitle="Last 30 days"
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<LogIn size={35} />}
            title="Total Logins"
            value={formatNumber(data.summary.totalLogins)}
            subtitle="Unique daily logins"
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Coins size={35} />}
            title="Credits Used"
            value={formatCredits(data.summary.totalCredits)}
            subtitle="Platform-wide"
            color="warning.main"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Mail size={35} />}
            title="Magic Links Sent"
            value={formatNumber(data.summary.totalMagicLinks)}
            subtitle={`${data.summary.totalConversions} used (${data.summary.conversionRate}%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Activity size={35} />}
            title="API Requests"
            value={formatNumber(data.summary.totalApiRequests)}
            subtitle="Total requests"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<AlertCircle size={35} />}
            title="Errors"
            value={formatNumber(data.summary.totalErrors)}
            subtitle={`${data.summary.errorRate}% error rate`}
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<TrendingUp size={35} />}
            title="Conversion Rate"
            value={`${data.summary.conversionRate}%`}
            subtitle="Magic link to login"
            color="success.main"
          />
        </Grid>
      </Grid>

      {/* Feature Totals */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<ListOrdered size={35} />}
            title="Smart Lists"
            value={formatNumber(data.summary.totalSmartLists)}
            subtitle="Total lists"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<TableIcon size={35} />}
            title="Smart Rows"
            value={formatNumber(data.summary.totalSmartRows)}
            subtitle="Total rows"
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Bot size={35} />}
            title="Agents"
            value={formatNumber(data.summary.totalAgents)}
            subtitle="Total agents"
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<MessageCircle size={35} />}
            title="Agent Conversations"
            value={formatNumber(data.summary.totalAgentConversations)}
            subtitle="Total conversations"
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<ListVideo size={35} />}
            title="Sequences"
            value={formatNumber(data.summary.totalSequences)}
            subtitle="Total sequences"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<ListOrdered size={35} />}
            title="Sequence Steps"
            value={formatNumber(data.summary.totalSequenceSteps)}
            subtitle="Total steps"
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Users size={35} />}
            title="Sequence Members"
            value={formatNumber(data.summary.totalSequenceMembers)}
            subtitle="Total members"
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Play size={35} />}
            title="Sequence Executions"
            value={formatNumber(data.summary.totalSequenceExecutions)}
            subtitle="Total executions"
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Inbox size={35} />}
            title="Inboxes"
            value={formatNumber(data.summary.totalInboxes)}
            subtitle="Total inboxes"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Mail size={35} />}
            title="Inbox Messages"
            value={formatNumber(data.summary.totalInboxMessages)}
            subtitle="Total messages"
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Folder size={35} />}
            title="Knowledge Base"
            value={formatNumber(data.summary.totalDocuments)}
            subtitle="Total documents"
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Coins size={35} />}
            title="KB Tokens"
            value={formatNumber(data.summary.totalDocumentTokens)}
            subtitle="Total tokens"
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<StickyNote size={35} />}
            title="KB Characters"
            value={formatNumber(data.summary.totalDocumentCharacters)}
            subtitle="Total characters"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<FileText size={35} />}
            title="Content"
            value={formatNumber(data.summary.totalContent)}
            subtitle="Total content items"
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Lightbulb size={35} />}
            title="Content Strategies"
            value={formatNumber(data.summary.totalContentStrategies)}
            subtitle="Total strategies"
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Columns3 size={35} />}
            title="Content Pillars"
            value={formatNumber(data.summary.totalContentPillars)}
            subtitle="Total pillars"
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Megaphone size={35} />}
            title="Content Campaigns"
            value={formatNumber(data.summary.totalContentCampaigns)}
            subtitle="Total campaigns"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Image size={35} />}
            title="Images"
            value={formatNumber(data.summary.totalImages)}
            subtitle="Total images"
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<FileAudio size={35} />}
            title="Audio"
            value={formatNumber(data.summary.totalAudio)}
            subtitle="Total audio files"
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<FileVideo size={35} />}
            title="Videos"
            value={formatNumber(data.summary.totalVideos)}
            subtitle="Total videos"
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* User Activity Chart */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardHeader
          avatar={<ChartLine size={24} />}
          title="User Activity"
          subheader="Signups and logins over time"
        />
        <CardContent>
          <LineChart
            height={300}
            series={[
              {
                data: data.timeSeries.signups,
                label: "Signups",
                color: "#4caf50",
              },
              {
                data: data.timeSeries.logins,
                label: "Logins",
                color: "#1976d2",
              },
            ]}
            xAxis={[{ data: shortLabels, scaleType: "band" }]}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "top", horizontal: "middle" },
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Recent Signups and Logins Tables */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Recent Signups */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader
              avatar={<UserPlus size={24} />}
              title="Recent Signups"
              subheader="Last 50 user registrations"
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Signed Up</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.breakdown.recentSignups.map((user) => {
                      const primaryDisplay = user.displayName || user.email;
                      return (
                        <TableRow key={user.uid}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                {(primaryDisplay || "?")[0].toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                  {primaryDisplay}
                                </Typography>
                                {user.displayName && user.email && (
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: "block" }}>
                                    {user.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {user.createdDate ? new Date(user.createdDate).toLocaleDateString() : "-"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.createdDate ? new Date(user.createdDate).toLocaleTimeString() : ""}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Logins */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader
              avatar={<LogIn size={24} />}
              title="Recent Logins"
              subheader="Last 50 user logins"
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Logged In</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.breakdown.recentLogins.map((login, index) => {
                      const isOrphanedUser = login.email?.startsWith("(no user record");
                      const primaryDisplay = login.displayName || login.email;
                      return (
                        <TableRow key={index} sx={isOrphanedUser ? { backgroundColor: "warning.light" } : undefined}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: isOrphanedUser ? "warning.main" : undefined }}>
                                {isOrphanedUser ? "?" : (primaryDisplay || "?")[0].toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                  {primaryDisplay}
                                </Typography>
                                {login.displayName && login.email && (
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: "block" }}>
                                    {login.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {login.loginDate ? new Date(login.loginDate).toLocaleDateString() : "-"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {login.loginDate ? new Date(login.loginDate).toLocaleTimeString() : ""}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Credit Usage Chart */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardHeader
          avatar={<Coins size={24} />}
          title="Daily Credit Usage"
          subheader="Credits consumed per day"
        />
        <CardContent>
          <BarChart
            height={300}
            series={[
              {
                data: data.timeSeries.credits,
                label: "Credits",
                color: "#ff9800",
              },
            ]}
            xAxis={[{ data: shortLabels, scaleType: "band" }]}
            slotProps={{
              legend: { hidden: true },
            }}
          />
        </CardContent>
      </Card>

      {/* Inbox Messages Chart */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardHeader
          avatar={<Inbox size={24} />}
          title="Inbox Messages Received"
          subheader={inboxMessagesData ? `Last 3 days (hourly) - ${formatNumber(inboxMessagesData.summary.totalMessages)} total` : "Last 3 days"}
        />
        <CardContent>
          {inboxMessagesLoading ? (
            <LinearProgress />
          ) : inboxMessagesData ? (
            <BarChart
              height={300}
              series={[
                {
                  data: inboxMessagesData.timeSeries.messages,
                  label: "Messages",
                  color: "#1976d2",
                },
              ]}
              xAxis={[{
                data: inboxMessagesData.period.labels.map((label) => {
                  // Parse label format "2026-02-13 19:00"
                  const [datePart, timePart] = label.split(' ');
                  const [, month, day] = datePart.split('-');
                  const hour = timePart.split(':')[0];
                  // Show date + hour for each bar
                  return `${month}/${day} ${hour}:00`;
                }),
                scaleType: "band",
                tickLabelStyle: {
                  angle: 45,
                  textAnchor: 'start',
                  fontSize: 10,
                },
              }]}
              slotProps={{
                legend: { hidden: true },
              }}
              margin={{ bottom: 70 }}
            />
          ) : (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </CardContent>
      </Card>

      {/* Magic Links Chart */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardHeader
          avatar={<Mail size={24} />}
          title="Magic Link Performance"
          subheader="Emails sent vs conversions"
        />
        <CardContent>
          <LineChart
            height={300}
            series={[
              {
                data: data.timeSeries.magicLinks,
                label: "Sent",
                color: "#9c27b0",
              },
              {
                data: data.timeSeries.conversions,
                label: "Used",
                color: "#4caf50",
              },
            ]}
            xAxis={[{ data: shortLabels, scaleType: "band" }]}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "top", horizontal: "middle" },
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Breakdown Section */}
      <Grid container spacing={3}>
        {/* Subscription Distribution */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader title="Subscription Distribution" subheader="Paid subscriptions only" />
            <CardContent>
              {data.breakdown.subscriptions.filter((sub) => sub.type && sub.type !== "free").length > 0 ? (
                <PieChart
                  height={300}
                  series={[
                    {
                      data: data.breakdown.subscriptions
                        .filter((sub) => sub.type && sub.type !== "free")
                        .map((sub, index) => ({
                          id: index,
                          value: sub.count,
                          label: sub.type,
                        })),
                      highlightScope: { faded: "global", highlighted: "item" },
                    },
                  ]}
                  colors={subscriptionColors}
                  slotProps={{
                    legend: {
                      direction: "column",
                      position: { vertical: "middle", horizontal: "right" },
                    },
                  }}
                />
              ) : (
                <Typography color="text.secondary">No subscription data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Feature Usage */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader
              avatar={<Cpu size={24} />}
              title="Top Features by Credits"
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Feature</TableCell>
                      <TableCell align="right">Credits</TableCell>
                      <TableCell align="right">Requests</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.breakdown.features.map((feature, index) => (
                      <TableRow key={index}>
                        <TableCell>{feature.feature}</TableCell>
                        <TableCell align="right">{formatCredits(feature.credits)}</TableCell>
                        <TableCell align="right">{formatNumber(feature.requests)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* AI Model Usage - Full Width Row */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader title="AI Model Usage" />
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="right">Requests</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.breakdown.models.map((model, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {model.model}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCredits(model.credits)}</TableCell>
                    <TableCell align="right">{formatNumber(model.tokens)}</TableCell>
                    <TableCell align="right">{formatNumber(model.requests)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Top Users - Full Width Row */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader title="Top 50 Users by Credit Usage" />
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell align="right">Requests</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.breakdown.topUsers.map((user) => {
                  const isOrphanedUser = user.email?.startsWith("(no user record:");
                  const primaryDisplay = user.displayName || user.email;
                  return (
                  <TableRow key={user.uid} sx={isOrphanedUser ? { backgroundColor: "warning.light" } : undefined}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: isOrphanedUser ? "warning.main" : undefined }}>
                          {isOrphanedUser ? "?" : (primaryDisplay || "?")[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {primaryDisplay}
                          </Typography>
                          {user.displayName && user.email && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: "block" }}>
                              {user.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatCredits(user.credits)}</TableCell>
                    <TableCell align="right">{formatNumber(user.requests)}</TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* API Health Section with Date Filter */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader
          avatar={<Activity size={24} />}
          title="API Health"
          subheader={apiHealthData ? `${new Date(apiHealthData.period.start).toLocaleDateString()} - ${new Date(apiHealthData.period.end).toLocaleDateString()}` : "Requests and errors over time"}
          action={
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={apiHealthDays}
                label="Time Range"
                onChange={(e) => setApiHealthDays(e.target.value as number)}
              >
                <MenuItem value={1}>Last 24 hours</MenuItem>
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </FormControl>
          }
        />
        <CardContent>
          {apiHealthLoading ? (
            <LinearProgress />
          ) : apiHealthData ? (
            <>
              <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                <Typography variant="body2">
                  Total Requests: <strong>{formatNumber(apiHealthData.summary.totalApiRequests)}</strong>
                </Typography>
                <Typography variant="body2">
                  Total Errors: <strong>{formatNumber(apiHealthData.summary.totalErrors)}</strong>
                </Typography>
                <Typography variant="body2">
                  Error Rate: <strong>{apiHealthData.summary.errorRate}%</strong>
                </Typography>
              </Box>
              <LineChart
                height={300}
                series={[
                  {
                    data: apiHealthData.timeSeries.apiRequests.map((v) => v / 100),
                    label: "Requests (x100)",
                    color: "#1976d2",
                  },
                  {
                    data: apiHealthData.timeSeries.errors,
                    label: "Errors",
                    color: "#d32f2f",
                  },
                ]}
                xAxis={[{
                  data: apiHealthData.period.labels.map((label) => {
                    const date = new Date(label);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }),
                  scaleType: "band"
                }]}
                slotProps={{
                  legend: {
                    direction: "row",
                    position: { vertical: "top", horizontal: "middle" },
                  },
                }}
              />
            </>
          ) : (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </CardContent>
      </Card>

      {/* Top Error Endpoints - Full Width Row */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader
          avatar={<AlertCircle size={24} />}
          title="Top 50 Error Endpoints"
          subheader={apiHealthData ? `Last ${apiHealthDays} day${apiHealthDays > 1 ? 's' : ''} - Most frequent API errors (4xx and 5xx)` : "Most frequent API errors (4xx and 5xx)"}
        />
        <CardContent sx={{ p: 0 }}>
          {apiHealthLoading ? (
            <Box sx={{ p: 2 }}><LinearProgress /></Box>
          ) : apiHealthData && apiHealthData.topErrorEndpoints.length > 0 ? (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Method</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiHealthData.topErrorEndpoints.map((endpoint, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={endpoint.method}
                          size="small"
                          color={endpoint.method === "GET" ? "primary" : endpoint.method === "POST" ? "info" : endpoint.method === "DELETE" ? "error" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                          {endpoint.path}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={endpoint.statusCode}
                          size="small"
                          color={endpoint.statusCode >= 500 ? "error" : "warning"}
                        />
                      </TableCell>
                      <TableCell align="right">{formatNumber(endpoint.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary">No errors in this time period</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Public API Usage Section */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader
          avatar={<Activity size={24} />}
          title="Public API Usage"
          subheader={publicApiData ? `${new Date(publicApiData.period.start).toLocaleDateString()} - ${new Date(publicApiData.period.end).toLocaleDateString()}` : "API v0 endpoint usage"}
          action={
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={publicApiDays}
                label="Time Range"
                onChange={(e) => setPublicApiDays(e.target.value as number)}
              >
                <MenuItem value={1}>Last 24 hours</MenuItem>
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </FormControl>
          }
        />
        <CardContent>
          {publicApiLoading ? (
            <LinearProgress />
          ) : publicApiData ? (
            <>
              <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                <Typography variant="body2">
                  Total Requests: <strong>{formatNumber(publicApiData.summary.totalRequests)}</strong>
                </Typography>
                <Typography variant="body2">
                  Total Errors: <strong>{formatNumber(publicApiData.summary.totalErrors)}</strong>
                </Typography>
                <Typography variant="body2">
                  Error Rate: <strong>{publicApiData.summary.errorRate}%</strong>
                </Typography>
              </Box>
              <BarChart
                height={250}
                series={[
                  {
                    data: publicApiData.timeSeries.requests,
                    label: "API Requests",
                    color: "#9c27b0",
                  },
                ]}
                xAxis={[{
                  data: publicApiData.period.labels.map((label) => {
                    const date = new Date(label);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }),
                  scaleType: "band"
                }]}
                slotProps={{
                  legend: { hidden: true },
                }}
              />
            </>
          ) : (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </CardContent>
      </Card>

      {/* Top Public API Endpoints */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader
          avatar={<Activity size={24} />}
          title="Top Public API Endpoints"
          subheader={publicApiData ? `Last ${publicApiDays} day${publicApiDays > 1 ? 's' : ''} - Most used /api/v0/ endpoints` : "Most used /api/v0/ endpoints"}
        />
        <CardContent sx={{ p: 0 }}>
          {publicApiLoading ? (
            <Box sx={{ p: 2 }}><LinearProgress /></Box>
          ) : publicApiData && publicApiData.topEndpoints.length > 0 ? (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Method</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Errors</TableCell>
                    <TableCell align="right">Avg Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publicApiData.topEndpoints.map((endpoint, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={endpoint.method}
                          size="small"
                          color={endpoint.method === "GET" ? "primary" : endpoint.method === "POST" ? "info" : endpoint.method === "DELETE" ? "error" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                          {endpoint.path}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(endpoint.requests)}</TableCell>
                      <TableCell align="right">
                        {endpoint.errors > 0 ? (
                          <Chip label={formatNumber(endpoint.errors)} size="small" color="error" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{endpoint.avgDurationMs}ms</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary">No public API usage in this time period</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Top Public API Users */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader
          avatar={<User size={24} />}
          title="Top Public API Users"
          subheader={publicApiData ? `Last ${publicApiDays} day${publicApiDays > 1 ? 's' : ''} - Users with most /api/v0/ requests` : "Users with most /api/v0/ requests"}
        />
        <CardContent sx={{ p: 0 }}>
          {publicApiLoading ? (
            <Box sx={{ p: 2 }}><LinearProgress /></Box>
          ) : publicApiData && publicApiData.topUsers.length > 0 ? (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Requests</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publicApiData.topUsers.map((user) => {
                    const isOrphanedUser = user.email?.startsWith("(no user record");
                    const primaryDisplay = user.displayName || user.email;
                    return (
                      <TableRow key={user.uid} sx={isOrphanedUser ? { backgroundColor: "warning.light" } : undefined}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: isOrphanedUser ? "warning.main" : undefined }}>
                              {isOrphanedUser ? "?" : (primaryDisplay || "?")[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                {primaryDisplay}
                              </Typography>
                              {user.displayName && user.email && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: "block" }}>
                                  {user.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatNumber(user.requests)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary">No public API users in this time period</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Top Public API Companies */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader
          avatar={<Building2 size={24} />}
          title="Top Public API Companies"
          subheader={publicApiData ? `Last ${publicApiDays} day${publicApiDays > 1 ? 's' : ''} - Companies with most /api/v0/ requests` : "Companies with most /api/v0/ requests"}
        />
        <CardContent sx={{ p: 0 }}>
          {publicApiLoading ? (
            <Box sx={{ p: 2 }}><LinearProgress /></Box>
          ) : publicApiData && publicApiData.topCompanies.length > 0 ? (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell align="right">Requests</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publicApiData.topCompanies.map((company) => {
                    const isOrphanedCompany = company.name?.startsWith("(no company record");
                    return (
                      <TableRow key={company.companyId} sx={isOrphanedCompany ? { backgroundColor: "warning.light" } : undefined}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: isOrphanedCompany ? "warning.main" : "primary.main" }}>
                              {isOrphanedCompany ? "?" : (company.name || "?")[0].toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                              {company.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatNumber(company.requests)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary">No public API company usage in this time period</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Whitelabel Leaderboard */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardHeader
          avatar={<Trophy size={24} />}
          title="Whitelabel Leaderboard"
          subheader={whitelabelData ? `${whitelabelData.totalWhitelabels} active whitelabels` : "Loading..."}
        />
        <CardContent sx={{ p: 0 }}>
          {whitelabelLoading ? (
            <Box sx={{ p: 2 }}><LinearProgress /></Box>
          ) : whitelabelData && whitelabelData.leaderboard.length > 0 ? (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell align="right">Companies</TableCell>
                    <TableCell align="right">Users</TableCell>
                    <TableCell align="right">Last Month</TableCell>
                    <TableCell align="right">This Month</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {whitelabelData.leaderboard.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {entry.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {entry.domain || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(entry.companies)}</TableCell>
                      <TableCell align="right">{formatNumber(entry.users)}</TableCell>
                      <TableCell align="right">{formatCredits(entry.creditsLastMonth)}</TableCell>
                      <TableCell align="right">{formatCredits(entry.creditsCurrentMonth)}</TableCell>
                      <TableCell align="right">{formatCredits(entry.creditsUsed)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary">No whitelabels found</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminAnalytics;
